import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, media } from "@/lib/api";
import { FlaskConical, ArrowRight, ShieldCheck, Clock, BookOpen } from "lucide-react";
import { Section, KV, Bullets, Steps, References, FAQ, RelatedPeptides } from "@/components/peptide/GuideSections";
import { ReconWidget } from "@/components/peptide/ReconWidget";
import { QuickNav } from "@/components/peptide/QuickNav";

const NAV = [
  ["quickstart", "Quickstart Overview"], ["science", "Scientific Data"], ["reconstitution", "Dosing & Reconstitution"],
  ["supplies", "Supplies Needed"], ["protocol", "Protocol Overview"], ["dosing", "Dosing Protocol"], ["storage", "Storage Instructions"],
  ["notes", "Important Notes"], ["mechanism", "How This Works"], ["benefits", "Benefits & Side Effects"], ["lifestyle", "Lifestyle Factors"],
  ["injection", "Injection Technique"], ["faq", "FAQ"], ["verified", "CG Labz Verified"], ["disclaimer", "Disclaimer"], ["references", "References"],
];

export default function PeptideDetail() {
  const { slug } = useParams();
  const [pep, setPep] = useState(null);
  const [faq, setFaq] = useState(null);

  useEffect(() => {
    setPep(null);
    api.get(`/peptides/${slug}`).then(({ data }) => setPep(data)).catch(() => {});
    window.scrollTo(0, 0);
  }, [slug]);

  if (!pep) return <div className="pt-40 text-center text-slate-500 font-mono text-sm">Loading peptide…</div>;
  const g = pep.guide;
  const nav = NAV.filter(([id]) => g || ["science", "verified", "disclaimer"].includes(id)).map(([id, label]) => ({ id, label }));
  const readMins = g ? Math.max(4, Math.round(JSON.stringify(g).length / 1200)) : 2;

  const sci = [
    ["Molecular Formula", pep.molecular_formula], ["Molecular Weight", pep.molecular_weight ? `${pep.molecular_weight} Da` : "—"],
    ["Sequence", pep.sequence], ["Sequence Length", pep.sequence_length || "—"], ["CAS", pep.cas_number || "—"],
    ["Peptide Family", pep.peptide_family], ["Half-life", pep.half_life_ref], ["Solubility", pep.solubility],
    ["Storage", pep.storage], ["Appearance", pep.appearance],
  ];

  return (
    <div className="px-4 sm:px-8 lg:px-12 pt-28 pb-20 max-w-7xl mx-auto">
      <div className="mono-label mb-3">Peptide Library / {pep.categories?.join(" / ")}</div>
      <header className="max-w-4xl">
        <h1 className="font-serif text-5xl sm:text-6xl text-white">{pep.name}</h1>
        <p className="mt-2 text-slate-500 font-mono text-sm">{pep.scientific_name}</p>
        {g && <p className="mt-4 text-cyan-200/80 text-lg font-light">{g.title}</p>}
        <div className="mt-4 flex flex-wrap items-center gap-4 text-[11px] font-mono text-slate-500">
          <span className="flex items-center gap-1.5"><BookOpen size={12} /> Complete Research Guide</span>
          <span className="flex items-center gap-1.5"><Clock size={12} /> {readMins} min read</span>
          {g?.references && <span>{g.references.length} peer-reviewed references</span>}
        </div>
      </header>

      <div className="grid lg:grid-cols-12 gap-10 mt-10">
        <div className="lg:col-span-8 space-y-6">
          {g ? (
            <Section id="quickstart" title="Quickstart Overview">
              <div className="grid sm:grid-cols-[1fr_auto] gap-6">
                <p className="text-slate-300 leading-relaxed">{g.quickstart.summary}</p>
                {(pep.image || pep.products?.[0]?.image) && (
                  <img src={media(pep.image || pep.products[0].image)} alt={pep.name} className="w-40 h-40 object-cover rounded-xl bg-black/30 border border-white/10 hidden sm:block" data-testid="peptide-hero-image" />
                )}
              </div>
              <div className="mt-6"><KV rows={g.quickstart.bullets} /></div>
            </Section>
          ) : (
            <Section id="quickstart" title="Overview"><p className="text-slate-300 leading-relaxed">{pep.overview}</p></Section>
          )}

          <Section id="science" title="Scientific Data"><KV rows={sci} /></Section>

          {g && <>
            <Section id="reconstitution" title="Dosing & Reconstitution Guide" kicker={`Standard: ${g.reconstitution.vial_mg} mg vial · ${g.reconstitution.default_water_ml} mL diluent`}>
              <ReconWidget recon={g.reconstitution} slug={pep.slug} />
              <div className="mt-6 text-sm text-slate-300"><span className="text-slate-500 font-mono text-xs uppercase tracking-widest">Frequency:</span> {g.reconstitution.frequency}</div>
              <h3 className="font-serif text-xl text-white mt-6 mb-4">Reconstitution Steps</h3>
              <Steps items={g.reconstitution.steps} />
            </Section>

            <Section id="supplies" title="Supplies Needed">
              <p className="text-sm text-slate-400 mb-5">{g.supplies.plan}</p>
              <KV rows={g.supplies.items} />
            </Section>

            <Section id="protocol" title="Protocol Overview"><KV rows={g.protocol_overview} /></Section>
            <Section id="dosing" title="Dosing Protocol"><KV rows={g.dosing} /></Section>
            <Section id="storage" title="Storage Instructions"><KV rows={g.storage} /></Section>
            <Section id="notes" title="Important Notes"><Bullets items={g.important_notes} icon="warn" tone="amber" /></Section>

            <Section id="mechanism" title="How This Works">
              <p className="text-slate-300 leading-relaxed text-[15px]">{g.how_it_works}</p>
            </Section>

            <Section id="benefits" title="Potential Benefits & Side Effects">
              <div className="grid md:grid-cols-2 gap-8">
                <div><div className="mono-label mb-3 text-cyan-300">Reported in research</div><Bullets items={g.benefits} /></div>
                <div><div className="mono-label mb-3 text-rose-400">Possible considerations</div><Bullets items={g.side_effects} icon="warn" tone="rose" /></div>
              </div>
            </Section>

            <Section id="lifestyle" title="Lifestyle Factors"><Bullets items={g.lifestyle} /></Section>
            <Section id="injection" title="Injection Technique"><Steps items={g.injection_technique} /></Section>
            {g.faq?.length > 0 && <Section id="faq" title="Frequently Asked Questions"><FAQ items={g.faq} open={faq} setOpen={setFaq} /></Section>}
          </>}

          <Section id="verified" title="CG Labz Verified Batches" kicker="Third-party HPLC testing">
            {pep.coas?.length ? (
              <div className="space-y-3">
                {pep.coas.slice(0, 3).map((c) => (
                  <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 border border-white/10 rounded-xl p-4">
                    <div>
                      <div className="font-mono text-sm text-white">{c.batch}</div>
                      <div className="text-[11px] font-mono text-slate-500 mt-0.5">{c.laboratory} · {c.coa_id}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-lg text-cyan-300">{c.purity_pct}%</div>
                      <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">HPLC Purity</div>
                    </div>
                    <Link to={`/verify/${c.batch}`} className="text-xs font-mono text-cyan-400 hover:text-cyan-300">Verify →</Link>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-slate-500">No published batches yet.</p>}
          </Section>

          <Section id="disclaimer" title="Research Disclaimer">
            <div className="flex gap-3 text-sm text-slate-300 leading-relaxed">
              <ShieldCheck size={18} className="text-cyan-300 shrink-0 mt-0.5" />
              <p>{g?.disclaimer || "All compounds are supplied strictly for laboratory research. CG Labz does not provide medical advice."}</p>
            </div>
          </Section>

          {g?.references?.length > 0 && <Section id="references" title="References" kicker="Peer-reviewed literature"><References items={g.references} /></Section>}

          {pep.related?.length > 0 && <Section id="related" title="Related Compounds"><RelatedPeptides items={pep.related} /></Section>}
        </div>

        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-28 space-y-4">
            <div className="glass rounded-2xl p-6 text-center">
              <FlaskConical className="text-cyan-300 mx-auto mb-3" size={24} />
              <div className="font-serif text-2xl text-white">Shop {pep.name}</div>
              <p className="text-xs text-slate-400 mt-2">Select a strength. Compare price per mg. Verify the batch.</p>
              {pep.products?.[0] && (
                <Link to={`/shop/${pep.products[0].slug}`} data-testid="peptide-shop-btn"
                  className="mt-4 inline-flex items-center gap-2 px-6 py-2.5 rounded-full btn-primary text-sm">
                  View Product <ArrowRight size={14} />
                </Link>
              )}
              <Link to={`/tools/calculator/${pep.slug}`} className="mt-3 block text-xs font-mono text-cyan-300">Open Precision Calculator →</Link>
            </div>
            <QuickNav items={nav} />
            <div className="glass rounded-2xl p-6">
              <div className="mono-label mb-3">Research Categories</div>
              <div className="flex flex-wrap gap-2">
                {(pep.categories || []).map((c) => (
                  <Link key={c} to={`/peptides?category=${c}`} className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-mono text-slate-300 hover:border-cyan-300/40">{c}</Link>
                ))}
              </div>
              <p className="mt-4 text-[11px] font-mono text-slate-500 leading-relaxed">Taxonomy is for research navigation only. CG Labz does not make health claims.</p>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
